import { FC, useState } from 'react';

import { AlertDialogWrapper } from '../../../../../components/alert-dialog/index';
import { SelectedCompanyCurrency } from '../../../../../components/selected-company-currency/index';
import { EventHooks, useFormat, useModalState } from '../../../../../hooks/index';
import {
  EventTypeBackend,
  TransactionSignatureFields,
  ValuationEvent,
} from '../../../../../types/events.types';
import {
  downloadCompanyStorageFilesSequentially,
  openCompanyStorageUrlInNewTab,
} from '../../../../../utils/company-storage-url.util';
import { toMathRound } from '../../../../../utils/to-math-round.util';
import { toNumber } from '../../../../../utils/to-number.util';
import { EventSignatureStatusCell } from '../../../components/event-signature-status-cell';
import { EventTransactionFilesCell } from '../../../components/event-transaction-files-cell';
import { FilesModal } from '../../../modals/index';
import { BasicTable } from './basic-table';
import {
  getTransactionsTableStateProps,
  itemsToFetchMap,
  TableBodyProps,
} from './event-table-types.types';
import { PaginationItem } from './pagination-item';
import { SignatureStatusTd, Td } from './table-td';

export const Valuation: FC<TableBodyProps> = (props) => {
  const { format } = useFormat();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[0]);
  const [activeFilesModal, setActiveFilesModal] = useState<{
    createdAt?: Date;
    docusignFileLinks: string[];
    filesLinks: string[];
    stakeholderName: string;
  } | null>(null);

  const { items, totalPages, isLoading } = EventHooks.useTransactions<
    TransactionSignatureFields & ValuationEvent
  >({
    eventId: props.eventId,
    eventType: EventTypeBackend.VALUATION,
    companyId: props.companyId,
    currentPage,
    take: Number(itemsToFetch),
    dateFilter: props.selectedDateFilter,
    customDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
  });

  const tableStateProps = getTransactionsTableStateProps({
    selectedDateFilter: props.selectedDateFilter,
    selectedCustomDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
    isLoading,
    itemsCount: items.length,
    event: props,
    itemsToFetch: Number(itemsToFetch),
  });

  const {
    isOpen: isOpenFilesModal,
    handleCloseModal: handleCloseFilesModal,
    handleOpenModal: handleOpenFilesModal,
    toggler: filesModalToggler,
  } = useModalState();

  const handleDocumentClick = (link: string) => {
    void openCompanyStorageUrlInNewTab(props.companyId, link);
  };

  const handleDownloadAllDocuments = async (links: string[]) => {
    await downloadCompanyStorageFilesSequentially(props.companyId, links);
  };

  const handleOpenFilesModalForRow = (
    filesLinks: string[],
    stakeholderName: string,
    date: Date,
    docusignFileLinks: string[],
  ) => {
    setActiveFilesModal({
      filesLinks,
      stakeholderName,
      createdAt: date,
      docusignFileLinks,
    });
    handleOpenFilesModal();
  };

  const handleCloseFilesModalWithReset = () => {
    setActiveFilesModal(null);
    handleCloseFilesModal();
  };

  if (props.type !== EventTypeBackend.VALUATION) return <></>;

  const Transactions = (
    <>
      {items.map((event) => {
        if (event.type !== EventTypeBackend.VALUATION) return null;

        const {
          sharePrice,
          issuedSharesOnStart,
          name,
          filesLinks,
          date,
          signatureSignedCount,
          signatureStatus,
          signatureTotalCount,
        } = event;
        const roundedSharePrice = toMathRound(sharePrice);

        return (
          <tr key={event.eventId}>
            <Td value={format(date, 'dd/MM/yyyy')} />
            <Td value={name} />
            <Td
              value={
                <>
                  <SelectedCompanyCurrency />
                  {roundedSharePrice.toLocaleString('en-US')}
                </>
              }
            />
            <Td value={(toNumber(issuedSharesOnStart) * sharePrice).toLocaleString('en-US')} />
            {props.transactionView && (
              <SignatureStatusTd
                value={
                  <EventSignatureStatusCell
                    signatureSignedCount={signatureSignedCount}
                    signatureStatus={signatureStatus}
                    signatureTotalCount={signatureTotalCount}
                  />
                }
              />
            )}
            {!props.transactionView && (
              <Td
                value={
                  <EventTransactionFilesCell
                    date={date}
                    docusignFileLinks={event.docusignFileLinks}
                    filesLinks={filesLinks}
                    onDocumentClick={handleDocumentClick}
                    onOpenFilesModal={handleOpenFilesModalForRow}
                    parentEvent={props}
                    stakeholderName={name}
                  />
                }
              />
            )}
          </tr>
        );
      })}
    </>
  );

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: filesModalToggler, open: isOpenFilesModal }}>
        <FilesModal
          createdAt={activeFilesModal?.createdAt}
          docusignFileLinks={activeFilesModal?.docusignFileLinks}
          filesLinks={activeFilesModal?.filesLinks || []}
          name={props.name}
          onClose={handleCloseFilesModalWithReset}
          onDownloadFile={handleDocumentClick}
          onSuccess={() => handleDownloadAllDocuments(activeFilesModal?.filesLinks || [])}
          stakeholderName={activeFilesModal?.stakeholderName || ''}
        />
      </AlertDialogWrapper>
      <BasicTable
        eventType={EventTypeBackend.VALUATION}
        transactionView={props.transactionView}
        {...tableStateProps}
      >
        {Transactions}
      </BasicTable>
      <PaginationItem
        currentPage={currentPage}
        handleChangeCurrentPage={setCurrentPage}
        handleFetchAmountChange={setItemsToFetch}
        itemsCount={items.length}
        itemsToFetch={itemsToFetch}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
